import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import './CryptoCheckout.css';

const COIN_ORDER = ['USDT_TRC20', 'USDT_ERC20', 'BTC'];

// QR code via public API (programming utility — generates QR image for wallet address)
const qrUrl = (address, size = 180) =>
  `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(address)}&size=${size}x${size}&bgcolor=0c1120&color=ffffff&format=png&margin=10`;

export default function CryptoCheckout() {
  const [params]                  = useSearchParams();
  const navigate                  = useNavigate();
  const { user }                  = useAuth();

  const planId          = params.get('planId');
  const platform        = params.get('platform');
  const planName        = params.get('planName')   || 'Challenge';
  const planPrice       = Number(params.get('price') || 0);
  const simBalance      = Number(params.get('simBalance') || 0);

  const [wallets,        setWallets]        = useState({});
  const [selectedCoin,   setSelectedCoin]   = useState('USDT_TRC20');
  const [txHash,         setTxHash]         = useState('');
  const [transactionId,  setTransactionId]  = useState(null);
  const [step,           setStep]           = useState('select');  // select | pay | submitted
  const [loading,        setLoading]        = useState(false);
  const [copied,         setCopied]         = useState(false);
  const [statusPollId,   setStatusPollId]   = useState(null);
  const [paymentStatus,  setPaymentStatus]  = useState(null);
  const [challengeId,    setChallengeId]    = useState(null);
  const [initLoading,    setInitLoading]    = useState(false);

  // Fetch wallet info
  useEffect(() => {
    api.get('/payments/crypto-wallets')
      .then(r => setWallets(r.data.wallets))
      .catch(() => toast.error('Failed to load wallet info'));
  }, []);

  // Poll payment status after submission
  const pollStatus = useCallback(async () => {
    if (!transactionId) return;
    try {
      const { data } = await api.get(`/payments/crypto-status/${transactionId}`);
      setPaymentStatus(data.status);
      if (data.status === 'completed' && data.challengeId) {
        setChallengeId(data.challengeId);
        clearInterval(statusPollId);
      }
    } catch (_) {}
  }, [transactionId, statusPollId]);

  useEffect(() => {
    if (step !== 'submitted' || !transactionId) return;
    const id = setInterval(pollStatus, 10000);
    setStatusPollId(id);
    return () => clearInterval(id);
  }, [step, transactionId, pollStatus]);

  if (!planId || !platform) {
    return (
      <div className="cc-page">
        <div className="cc-error">
          <div style={{ fontSize: 48 }}>⚠️</div>
          <h2>Invalid Checkout Link</h2>
          <p>Missing plan or platform information.</p>
          <Link to="/pricing" className="btn btn-primary">Back to Pricing</Link>
        </div>
      </div>
    );
  }

  const wallet = wallets[selectedCoin];

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleInitPayment = async () => {
    if (!wallet) return;
    setInitLoading(true);
    try {
      const { data } = await api.post('/payments/crypto-init', {
        planId,
        cryptoCoin: selectedCoin,
        tradingPlatform: platform,
      });
      setTransactionId(data.transactionId);
      setStep('pay');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to initiate payment');
    } finally {
      setInitLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!txHash.trim()) return toast.error('Please enter your transaction hash');
    if (!transactionId)  return toast.error('Please proceed to the payment step first');
    setLoading(true);
    try {
      await api.post('/payments/crypto-submit', { transactionId, txHash: txHash.trim() });
      setStep('submitted');
      toast.success('Payment submitted! Our team will verify within 1–3 hours.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cc-page">
      {/* Nav */}
      <nav className="cc-nav">
        <Link to="/" className="cc-nav-logo">▲ Alpha<span>Vault</span></Link>
        <div className="cc-nav-right">
          <div className="cc-nav-user">
            <div className="cc-nav-avatar">{user?.name?.[0]?.toUpperCase() || 'U'}</div>
            <span>{user?.name}</span>
          </div>
        </div>
      </nav>

      <div className="cc-body">
        {/* Left — checkout panel */}
        <div className="cc-main">

          {/* Progress steps */}
          <div className="cc-steps">
            {['Choose Coin', 'Send Payment', 'Confirmation'].map((s, i) => {
              const stepIndex = step === 'select' ? 0 : step === 'pay' ? 1 : 2;
              return (
                <div key={s} className={`cc-step ${stepIndex >= i ? 'active' : ''} ${stepIndex > i ? 'done' : ''}`}>
                  <div className="cc-step-num">{stepIndex > i ? '✓' : i + 1}</div>
                  <span>{s}</span>
                  {i < 2 && <div className={`cc-step-line ${stepIndex > i ? 'done' : ''}`} />}
                </div>
              );
            })}
          </div>

          <AnimatePresence mode="wait">

            {/* ── Step 1: Select coin ───────────────────────────────────────── */}
            {step === 'select' && (
              <motion.div key="select" className="cc-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                <h2 className="cc-card-title">Select Payment Coin</h2>
                <p className="cc-card-sub">Choose your preferred cryptocurrency for payment. All coins are accepted at the USD equivalent.</p>

                <div className="cc-coin-list">
                  {COIN_ORDER.map(key => {
                    const w = wallets[key];
                    if (!w) return null;
                    return (
                      <motion.button
                        key={key}
                        className={`cc-coin-card ${selectedCoin === key ? 'active' : ''}`}
                        onClick={() => setSelectedCoin(key)}
                        whileHover={{ scale: 1.01 }}
                        style={{ '--coin-color': w.color }}
                      >
                        <div className="cc-coin-icon" style={{ background: `${w.color}18`, color: w.color }}>{w.icon}</div>
                        <div className="cc-coin-info">
                          <div className="cc-coin-label">{w.label}</div>
                          <div className="cc-coin-network">{w.network}</div>
                        </div>
                        <div className="cc-coin-right">
                          <div className="cc-coin-amount" style={{ color: w.color }}>${planPrice} USD</div>
                          {selectedCoin === key && <div className="cc-coin-check" style={{ background: w.color }}>✓</div>}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>

                {wallet && (
                  <div className="cc-coin-warning">
                    <span className="cc-warn-icon">⚠️</span>
                    <span>{wallet.note}</span>
                  </div>
                )}

                <button
                  className="btn btn-primary cc-proceed-btn"
                  onClick={handleInitPayment}
                  disabled={initLoading || !wallet}
                >
                  {initLoading ? 'Preparing…' : `Continue with ${wallet?.label || '…'} →`}
                </button>
              </motion.div>
            )}

            {/* ── Step 2: Send payment ──────────────────────────────────────── */}
            {step === 'pay' && wallet && (
              <motion.div key="pay" className="cc-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                <h2 className="cc-card-title">Send Payment</h2>
                <p className="cc-card-sub">Send exactly <strong style={{ color: wallet.color }}>${planPrice} USD</strong> worth of {wallet.coin} to the address below.</p>

                <div className="cc-pay-layout">
                  {/* QR code */}
                  <div className="cc-qr-wrap">
                    <div className="cc-qr-box" style={{ borderColor: `${wallet.color}44` }}>
                      <img
                        src={qrUrl(wallet.address)}
                        alt={`${wallet.label} QR Code`}
                        className="cc-qr-img"
                        loading="lazy"
                      />
                    </div>
                    <div className="cc-qr-label" style={{ color: wallet.color }}>{wallet.coin} · {wallet.network}</div>
                  </div>

                  {/* Address + instructions */}
                  <div className="cc-pay-right">
                    <div className="cc-amount-row">
                      <span className="cc-amount-label">Amount to Send</span>
                      <div className="cc-amount-val" style={{ color: wallet.color }}>
                        <span>${planPrice} USD</span>
                        <span className="cc-amount-coin">in {wallet.coin}</span>
                      </div>
                    </div>

                    <div className="cc-address-block">
                      <div className="cc-address-label">
                        <span className="cc-network-badge" style={{ background: `${wallet.color}22`, color: wallet.color, borderColor: `${wallet.color}44` }}>
                          {wallet.icon} {wallet.network}
                        </span>
                        Send to address:
                      </div>
                      <div className="cc-address-box">
                        <span className="cc-address-text mono">{wallet.address}</span>
                        <button
                          className={`cc-copy-btn ${copied ? 'copied' : ''}`}
                          onClick={() => handleCopy(wallet.address)}
                          style={{ '--c': wallet.color }}
                        >
                          {copied ? '✓ Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>

                    <div className="cc-steps-list">
                      {['Copy the wallet address above', `Send exactly $${planPrice} USD in ${wallet.coin} (${wallet.network})`, 'Wait for blockchain confirmation (1–5 min)', 'Copy your transaction hash (TxID)', 'Paste it below and submit'].map((s, i) => (
                        <div key={i} className="cc-inst-row">
                          <div className="cc-inst-num" style={{ background: `${wallet.color}18`, color: wallet.color }}>{i + 1}</div>
                          <span>{s}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* TX Hash input */}
                <div className="cc-txhash-section">
                  <div className="cc-txhash-label">Transaction Hash (TxID)</div>
                  <div className="cc-txhash-input-wrap">
                    <input
                      className="cc-txhash-input mono"
                      placeholder="Paste your transaction hash here after sending payment…"
                      value={txHash}
                      onChange={e => setTxHash(e.target.value)}
                    />
                  </div>
                  <p className="cc-txhash-hint">
                    Find your TxID in your wallet's transaction history or on a block explorer after sending.
                  </p>
                </div>

                <div className="cc-pay-actions">
                  <button className="cc-back-btn" onClick={() => setStep('select')}>← Change Coin</button>
                  <button
                    className="btn btn-primary cc-submit-btn"
                    onClick={handleSubmit}
                    disabled={loading || !txHash.trim()}
                    style={{ background: `linear-gradient(135deg, ${wallet.color}cc, ${wallet.color})` }}
                  >
                    {loading ? 'Submitting…' : 'Submit Payment for Verification →'}
                  </button>
                </div>

                <div className="cc-security-note">
                  🔒 Your payment is verified manually by our team. Challenges are activated within 1–3 hours after blockchain confirmation.
                </div>
              </motion.div>
            )}

            {/* ── Step 3: Submitted ─────────────────────────────────────────── */}
            {step === 'submitted' && (
              <motion.div key="submitted" className="cc-card cc-submitted" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
                {challengeId ? (
                  <>
                    <div className="cc-success-icon">✅</div>
                    <h2 style={{ color: '#10b981' }}>Challenge Activated!</h2>
                    <p>Your payment has been verified and your funded account is ready.</p>
                    <Link to={`/challenge/${challengeId}`} className="btn btn-primary" style={{ marginTop: 20 }}>
                      Open My Challenge →
                    </Link>
                  </>
                ) : (
                  <>
                    <div className="cc-pending-icon">
                      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 2, ease: 'linear' }}>⏳</motion.div>
                    </div>
                    <h2>Payment Under Review</h2>
                    <p>Your transaction has been submitted. Our team is verifying it on the blockchain.</p>
                    <div className="cc-status-card">
                      <div className="cc-status-row"><span>Status</span><span className="badge-yellow">Awaiting Verification</span></div>
                      <div className="cc-status-row"><span>Transaction Hash</span><span className="mono small">{txHash.slice(0, 24)}…</span></div>
                      <div className="cc-status-row"><span>Coin</span><span>{wallet?.label}</span></div>
                      <div className="cc-status-row"><span>Amount</span><span className="mono">${planPrice} USD</span></div>
                      <div className="cc-status-row"><span>Platform</span><span>{platform}</span></div>
                    </div>
                    <p className="cc-eta">⏱ Estimated activation: <strong>1–3 hours</strong></p>
                    <p style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>
                      We'll notify you by email once your challenge is activated. You can close this page.
                    </p>
                    <Link to="/dashboard" className="cc-dash-link">Go to Dashboard →</Link>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right — order summary */}
        <div className="cc-sidebar">
          <div className="cc-summary-card">
            <div className="cc-summary-title">Order Summary</div>

            <div className="cc-summary-plan">
              <div className="cc-sp-icon">▲</div>
              <div>
                <div className="cc-sp-name">{planName}</div>
                <div className="cc-sp-sub">Prop Trading Challenge</div>
              </div>
            </div>

            <div className="cc-summary-rows">
              <div className="cc-sum-row"><span>Funded Account</span><span className="mono text-accent">${simBalance.toLocaleString()}</span></div>
              <div className="cc-sum-row"><span>Platform</span><span>{platform}</span></div>
              <div className="cc-sum-row"><span>Payout Split</span><span className="mono text-green">Up to 90%</span></div>
              <div className="cc-sum-row"><span>Payout Speed</span><span>12–24 hrs</span></div>
              <div className="cc-sum-row"><span>Fee Refund</span><span className="text-green">On 1st payout</span></div>
            </div>

            <div className="cc-sum-divider" />
            <div className="cc-sum-total">
              <span>Total</span>
              <span className="mono cc-sum-price">${planPrice} USD</span>
            </div>

            <div className="cc-sum-coins">
              <div className="cc-sum-coins-title">Accepted Payments</div>
              <div className="cc-accepted-coins">
                {Object.values(wallets).map(w => (
                  <div key={w.label} className="cc-accepted-coin" style={{ '--cc': w.color }}>
                    <span style={{ color: w.color }}>{w.icon}</span>
                    <span>{w.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="cc-sum-guarantees">
              {['Manual payment verification', 'Challenge activated within 1–3 hrs', 'Real-time challenge tracking', 'Fee refunded on first payout'].map(g => (
                <div key={g} className="cc-guarantee"><span>✓</span>{g}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
